import BaseContainer from '@/components/BaseContainer';
import Form from '@/components/Form/Form';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import FTextInput from '@/components/Form/TextInput';
import ScreenMargin from '@/components/ScreenMargin';
import useFormStyle from '@/components/Styles/useFormStyle';
import { useUserContext } from '@/components/Auth/UserContext';
import { DEFAULT_TITLES } from '@/hooks/useUserTitle';
import { useUpdateChirurgienMutation } from '@/data/data';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { useToast } from 'react-native-toast-notifications';
import { AvailableValidationRules } from '@/components/Form/validation';

type ProfilForm = {
  nom: string;
  prenom: string;
  titreFr: string;
  titreEn: string;
};

const ProfilPage = () => {
  const { t } = useTranslation();
  const { user, reloadUser, userIsLoading } = useUserContext();
  const toast = useToast();
  const styles = useFormStyle();
  const form = useForm<ProfilForm>();
  const [loading, setLoading] = useState(false);

  const [updateChirurgien] = useUpdateChirurgienMutation();

  useEffect(() => {
    if (user) {
      form.reset({
        nom: user.nom ?? '',
        prenom: user.prenom ?? '',
        titreFr: user.titreFr?.length ? user.titreFr : DEFAULT_TITLES.titreFr,
        titreEn: user.titreEn?.length ? user.titreEn : DEFAULT_TITLES.titreEn,
      });
    }
  }, [user?.id, user?.titreFr, user?.titreEn, user?.nom, user?.prenom]);

  if (userIsLoading || !user) {
    return (
      <BaseContainer>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text>{t('Chargement...')}</Text>
        </View>
      </BaseContainer>
    );
  }

  return (
    <ScreenMargin>
      <BaseContainer withScrollView={'keyboardAware'}>
        <Text style={styles?.headerForm}>{t('Mon profil')}</Text>

        <Form {...form}>
          <View style={styles?.bodyForm}>
            <View style={styles?.groupForm}>
              <Text style={styles?.titleGroupForm}>{t('Identité')}</Text>

              <FTextInput
                name="prenom"
                label={t('Prénom')}
                withMargin={false}
                topLabel={true}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <FTextInput
                name="nom"
                label={t('Nom')}
                withMargin={false}
                topLabel={true}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
            </View>

            <View style={styles?.groupForm}>
              <Text style={styles?.titleGroupForm}>{t('Titre')}</Text>

              <FTextInput
                name="titreFr"
                label={t('Titre (français)')}
                placeholder={'Dr.'}
                withMargin={false}
                topLabel={true}
                containerStyle={styles.input2Col}
              />
              <FTextInput
                name="titreEn"
                label={t('Title (English)')}
                placeholder={'MD'}
                withMargin={false}
                topLabel={true}
                containerStyle={styles.input2Col}
              />
            </View>
          </View>

          <View style={styles?.footerForm}>
            <FormSectionFooter
              saveLoading={loading}
              saveDisabled={loading}
              onPress={form.handleSubmit(async (data) => {
                setLoading(true);
                try {
                  await updateChirurgien({
                    variables: {
                      id: user.id,
                      data: {
                        nom: data.nom,
                        prenom: data.prenom,
                        titreFr: data.titreFr?.trim().length
                          ? data.titreFr.trim()
                          : DEFAULT_TITLES.titreFr,
                        titreEn: data.titreEn?.trim().length
                          ? data.titreEn.trim()
                          : DEFAULT_TITLES.titreEn,
                      },
                    },
                    fetchPolicy: 'no-cache',
                  });
                  await reloadUser();
                  toast.show(t('Profil mis à jour'), {
                    type: 'success',
                    placement: 'center',
                    duration: 1500,
                  });
                } catch (error: any) {
                  console.error('Erreur sauvegarde profil:', error);
                  toast.show(t('Erreur lors de la sauvegarde'), {
                    type: 'danger',
                    placement: 'center',
                    duration: 3000,
                  });
                } finally {
                  setLoading(false);
                }
              })}
            />
          </View>
        </Form>
      </BaseContainer>
    </ScreenMargin>
  );
};

export default ProfilPage;
